import { useCallback, useEffect, useState } from "react";
import { Autocomplete, Box, Button, CircularProgress, Popover, Stack, TextField, Typography } from "@mui/material";

import { formatMoney } from "../../../../../shared/utils/formatMoney";
import { formatUnitsPerSaleInput, orderSalesImportDescriptions, validateUnitsPerSale } from "../utils/salesImportStepTwoModel";
import SalesImportRegularizationDialog from "./SalesImportRegularizationDialog";

const numberFormatter = new Intl.NumberFormat("es-AR", { maximumFractionDigits: 10 });
const descriptionColumns = "minmax(155px, 1.6fr) minmax(60px, .5fr) minmax(105px, .85fr) minmax(190px, 1.65fr) minmax(92px, .7fr) minmax(75px, .6fr)";

function quantity(value) {
  const number = Number(value);
  return Number.isFinite(number) ? numberFormatter.format(number) : "—";
}

function Summary({ matching, preview }) {
  const total = matching?.summary.totalDescriptions ?? 0;
  const recognized = matching?.summary.resolvedDescriptions ?? 0;
  const pending = matching?.summary.pendingDescriptions ?? 0;
  const shortages = preview?.summary.shortageCount ?? 0;
  const rows = [
    [total, "Productos encontrados", "secondary.100"],
    [recognized, "Reconocidos", "success.light"],
    [pending, "No reconocidos", "warning.light"],
    [shortages, "Con Stock insuficiente", "error.light"],
  ];

  return (
    <Stack spacing={0.75} sx={{ p: 2, borderRadius: "24px", bgcolor: "secondary.200", border: "1px solid", borderColor: "neutral.600" }}>
      {rows.map(([value, label, color]) => (
        <Stack key={label} direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Box sx={{ minWidth: 52, py: 0.25, borderRadius: "7px", bgcolor: color, color: "primary.900", textAlign: "center", fontSize: 14, fontWeight: 800 }}>
            {quantity(value)}
          </Box>
          <Typography sx={{ color: "primary.900", fontSize: 14, fontWeight: 650 }}>{label}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function DescriptionRow({ item, options, optionsLoading, optionsError, productSearch, busy, rememberedResolutions, onSearch, onResolve, onPendingChange }) {
  const backendUnits = formatUnitsPerSaleInput(item.resolution.unitsPerSale) || "1";
  const remembered = rememberedResolutions.get(item.normalizedDescription);
  const [units, setUnits] = useState(remembered?.units || backendUnits);
  const [localError, setLocalError] = useState("");
  const [revertingNoStock, setRevertingNoStock] = useState(false);
  const noStock = item.resolution.kind === "NO_STOCK" && !revertingNoStock;
  const selected = noStock ? remembered?.product || null : item.resolution.saleProduct;
  const selectableOptions = selected && !options.some((option) => option.id === selected.id)
    ? [selected, ...options]
    : options;

  useEffect(() => {
    setUnits(item.resolution.kind === "NO_STOCK"
      ? rememberedResolutions.get(item.normalizedDescription)?.units || "1"
      : formatUnitsPerSaleInput(item.resolution.unitsPerSale) || "1");
    if (item.resolution.kind !== "NO_STOCK") setRevertingNoStock(false);
    onPendingChange(item.normalizedDescription, false);
  }, [item.normalizedDescription, item.resolution.kind, item.resolution.unitsPerSale, item.resolution.saleProduct?.id, rememberedResolutions, onPendingChange]);

  function saveProduct(product) {
    if (!product) return;
    const validationError = validateUnitsPerSale(units);
    setLocalError(validationError);
    if (validationError) return;
    onResolve(item.normalizedDescription, {
      kind: "SALE_PRODUCT",
      saleProductId: product.id,
      unitsPerSale: units.trim(),
    });
  }

  return (
    <Box role="row" sx={{ display: "grid", gridTemplateColumns: descriptionColumns, gap: 1.5, px: 1.5, py: 1.25, minHeight: 72, borderBottom: "1px solid", borderColor: "neutral.300", alignItems: "start", bgcolor: noStock ? "neutral.100" : "neutral.50" }}>
      <Typography role="cell" sx={{ color: "primary.900", fontSize: 12, overflowWrap: "anywhere", pt: 1.5 }}>{item.externalDescription}</Typography>
      <Typography role="cell" sx={{ color: "primary.900", fontSize: 12, pt: 1.5 }}>{quantity(item.soldQuantity)}</Typography>
      <Typography role="cell" sx={{ color: "primary.900", fontSize: 12, pt: 1.5 }}>{formatMoney(item.salesAmount)}</Typography>
      <Box role="cell" sx={{ minWidth: 0 }}>
        <Autocomplete
          size="small"
          disableClearable
          disabled={busy || noStock}
          options={selectableOptions}
          onOpen={() => onSearch("")}
          loading={optionsLoading}
          loadingText="Cargando Productos de venta…"
          noOptionsText={optionsError
            ? "No se pudieron cargar los Productos de venta. Usá Reintentar."
            : productSearch.trim()
              ? "No hay Productos de venta para esta búsqueda."
              : "No hay Productos de venta en la empresa."}
          value={selected}
          filterOptions={(items) => items}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option?.name || ""}
          onInputChange={(_event, value, reason) => {
            if (reason === "input") onSearch(value);
          }}
          onChange={(_event, product) => saveProduct(product)}
          renderOption={(props, product) => (
            <li {...props} key={product.id}>
              {product.name}{product.isActive ? "" : " · Inactivo"}{product.costStatus === "REQUIRES_REVIEW" ? " · Costo en revisión" : ""}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Seleccionar producto"
              inputProps={{ ...params.inputProps, "aria-label": `Producto para ${item.externalDescription}`, title: selected?.name || undefined }}
            />
          )}
        />
        {selected && !selected.isActive ? (
          <Typography sx={{ color: "warning.dark", fontSize: 11 }}>Producto inactivo</Typography>
        ) : null}
        {item.issues.filter((issue) => issue.severity === "BLOCKING" && issue.code !== "DESCRIPTION_REQUIRES_RESOLUTION").map((issue) => (
          <Typography key={issue.code} sx={{ color: "error.main", fontSize: 11 }}>{issue.message}</Typography>
        ))}
        <Button
          type="button"
          disabled={busy}
          onClick={() => {
            setLocalError("");
            if (noStock) {
              const previous = rememberedResolutions.get(item.normalizedDescription);
              if (previous?.product && !validateUnitsPerSale(previous.units)) {
                setUnits(previous.units);
                onResolve(item.normalizedDescription, {
                  kind: "SALE_PRODUCT",
                  saleProductId: previous.product.id,
                  unitsPerSale: previous.units,
                });
              } else {
                setRevertingNoStock(true);
                onPendingChange(item.normalizedDescription, true);
              }
            } else if (revertingNoStock) {
              setRevertingNoStock(false);
              onPendingChange(item.normalizedDescription, false);
            } else {
              if (selected) {
                rememberedResolutions.set(item.normalizedDescription, {
                  product: selected,
                  units: validateUnitsPerSale(units) ? backendUnits : units.trim(),
                });
              }
              onResolve(item.normalizedDescription, { kind: "NO_STOCK" });
            }
          }}
          sx={{ px: 0, minWidth: 0, textTransform: "none", fontSize: 11, color: "secondary.700" }}
        >
          {noStock ? "Afecta Stock" : "No afecta Stock"}
        </Button>
      </Box>
      <Box role="cell" sx={{ minWidth: 0 }}>
        <TextField
          size="small"
          value={units}
          disabled={busy || noStock}
          onChange={(event) => {
            setUnits(event.target.value);
            setLocalError("");
            onPendingChange(item.normalizedDescription, formatUnitsPerSaleInput(event.target.value) !== backendUnits || revertingNoStock);
          }}
          onBlur={() => {
            if (formatUnitsPerSaleInput(units) === backendUnits) {
              setUnits(backendUnits);
              return;
            }
            if (!selected) return;
            saveProduct(selected);
          }}
          error={Boolean(localError)}
          helperText={localError || undefined}
          inputProps={{ inputMode: "decimal", "aria-label": `Unidades por venta de ${item.externalDescription}` }}
          sx={{ width: "100%" }}
        />
      </Box>
      <Typography role="cell" sx={{ color: "primary.900", fontSize: 12, pt: 1.5 }}>
        {noStock ? "—" : item.resolution.effectiveQuantity ? quantity(item.resolution.effectiveQuantity) : "—"}
      </Typography>
    </Box>
  );
}

function DescriptionTable({ descriptions, descriptionOrder, options, optionsLoading, optionsError, productSearch, busy, rememberedResolutions, onSearch, onResolve, onPendingChange }) {
  if (!descriptions.length) {
    return <Typography sx={{ p: 2, color: "neutral.700" }}>No hay descripciones para mostrar.</Typography>;
  }
  return (
    <Box role="region" aria-label="Desplazamiento de descripciones" tabIndex={0} sx={{ height: "100%", minHeight: 0, overflow: "auto", scrollbarGutter: "stable", overscrollBehavior: "contain" }}>
      <Box role="table" aria-label="Descripciones del Excel" sx={{ minWidth: 780, bgcolor: "neutral.50" }}>
        <Box role="row" sx={{ display: "grid", gridTemplateColumns: descriptionColumns, gap: 1.5, px: 1.5, py: 1.25, bgcolor: "neutral.600", position: "sticky", top: 0, zIndex: 1 }}>
          {["Descripción del Excel", "Vendidos", "Importe", "Resolución", "Unidades por venta", "Consumo"].map((label) => (
            <Typography role="columnheader" key={label} sx={{ color: "logoblanco", fontSize: 12, fontWeight: 750 }}>{label}</Typography>
          ))}
        </Box>
        {orderSalesImportDescriptions(descriptions, descriptionOrder).map((item) => (
          <DescriptionRow
            key={item.normalizedDescription}
            item={item}
            options={options}
            optionsLoading={optionsLoading}
            optionsError={optionsError}
            productSearch={productSearch}
            busy={busy}
            rememberedResolutions={rememberedResolutions}
            onSearch={onSearch}
            onResolve={onResolve}
            onPendingChange={onPendingChange}
          />
        ))}
      </Box>
    </Box>
  );
}

function ShortagesTable({ shortages }) {
  const [originAnchor, setOriginAnchor] = useState(null);
  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("");

  if (!shortages.length) {
    return <Typography sx={{ p: 2, color: "neutral.700" }}>No hay faltantes de Stock.</Typography>;
  }
  return (
    <Box role="region" aria-label="Desplazamiento de Stock insuficiente" tabIndex={0} sx={{ height: "100%", minHeight: 0, overflow: "auto", scrollbarGutter: "stable", overscrollBehavior: "contain" }}>
      <Box role="table" aria-label="Stock insuficiente" sx={{ minWidth: 800, bgcolor: "neutral.50" }}>
        <Box role="row" sx={{ display: "grid", gridTemplateColumns: "1.5fr .8fr 1fr 1fr 1fr 1fr 1.3fr", gap: 1, p: 1.25, bgcolor: "neutral.600", position: "sticky", top: 0, zIndex: 1 }}>
          {["Ítem de Stock", "Tipo", "Stock actual", "Requerido", "A regularizar", "Stock final", "Afectado por"].map((label) => (
            <Typography role="columnheader" key={label} sx={{ color: "logoblanco", fontSize: 12, fontWeight: 750 }}>{label}</Typography>
          ))}
        </Box>
        {shortages.map((item) => (
          <Box role="row" key={`${item.itemType}-${item.itemId}`} sx={{ display: "grid", gridTemplateColumns: "1.5fr .8fr 1fr 1fr 1fr 1fr 1.3fr", gap: 1, p: 1.25, minHeight: 48, borderBottom: "1px solid", borderColor: "neutral.300", bgcolor: "neutral.50", alignItems: "center" }}>
            <Typography role="cell" sx={{ fontSize: 12 }}>{item.name}</Typography>
            <Typography role="cell" sx={{ fontSize: 12 }}>{item.itemType === "INGREDIENT" ? "Insumo" : "Elaborado"}</Typography>
            {[item.currentStock, item.requiredConsumption, item.regularization, item.projectedFinalStock].map((value, index) => (
              <Typography role="cell" key={index} sx={{ fontSize: 12 }}>{quantity(value)} {item.unit}</Typography>
            ))}
            <Box role="cell" sx={{ fontSize: 12 }}>
              {item.origins.length === 1 ? item.origins[0].saleProductName : item.origins.length > 1 ? (
                <Button
                  type="button"
                  onClick={(event) => {
                    setSelectedOrigins(item.origins);
                    setSelectedUnit(item.unit);
                    setOriginAnchor(event.currentTarget);
                  }}
                  sx={{ p: 0.5, minWidth: 0, border: "1px solid", borderColor: "neutral.600", borderRadius: "8px", textTransform: "none", color: "primary.900", fontSize: 12 }}
                >
                  {item.origins.length} productos
                </Button>
              ) : "—"}
            </Box>
          </Box>
        ))}
      </Box>
      <Popover
        open={Boolean(originAnchor)}
        anchorEl={originAnchor}
        onClose={() => setOriginAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{ sx: { p: 2, maxWidth: 360, borderRadius: "16px" } }}
      >
        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 800, color: "primary.900" }}>Productos que afectan el Stock</Typography>
          {selectedOrigins.map((origin) => (
            <Typography key={origin.saleProductId} sx={{ fontSize: 13, color: "primary.900" }}>
              {origin.saleProductName}: {quantity(origin.requiredConsumption)} {selectedUnit}
            </Typography>
          ))}
          <Button onClick={() => setOriginAnchor(null)} sx={{ alignSelf: "flex-end", textTransform: "none" }}>Cerrar</Button>
        </Stack>
      </Popover>
    </Box>
  );
}

export default function SalesImportStepTwo({ matching, preview, plan, descriptionOrder = [], loading, saving = false, error, notice, onRetry, options = [], optionsLoading = false, optionsError = "", productSearch = "", rememberedResolutions, onRetryOptions, onSearch, onResolve, onPrepare, onPendingEditChange }) {
  const [tab, setTab] = useState("descriptions");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingEdits, setPendingEdits] = useState({});
  const shortages = preview?.shortages || [];
  const planPrepared = Boolean(plan?.confirmedAt && plan.draftId === preview?.draftId && plan.previewRevision === preview?.previewRevision && plan.draftVersion === preview?.version);
  const hasPendingEdit = Object.values(pendingEdits).some(Boolean);
  const descriptionIssueKeys = new Set(
    (preview?.descriptions || []).flatMap((description) =>
      (description.issues || [])
        .filter((issue) => issue.severity === "BLOCKING")
        .map((issue) => `${description.normalizedDescription}|${issue.code}`)
    )
  );
  const additionalBlockingIssues = (preview?.issues || []).filter(
    (issue) =>
      issue.severity === "BLOCKING" &&
      issue.code !== "DESCRIPTION_REQUIRES_RESOLUTION" &&
      !descriptionIssueKeys.has(`${issue.normalizedDescription || ""}|${issue.code}`)
  );
  const handlePendingChange = useCallback((description, pending) => {
    setPendingEdits((current) => current[description] === pending ? current : { ...current, [description]: pending });
  }, []);

  useEffect(() => {
    onPendingEditChange?.(hasPendingEdit);
  }, [hasPendingEdit, onPendingEditChange]);

  useEffect(() => {
    if (loading) setPendingEdits({});
  }, [loading]);

  useEffect(() => {
    if (planPrepared) setConfirmOpen(false);
  }, [planPrepared]);

  return (
    <Stack role="region" aria-label="Contenido de Productos y Stock" tabIndex={0} spacing={1.75} sx={{ height: "100%", minHeight: 0, minWidth: 0, overflowY: { xs: "visible", md: "hidden" }, overflowX: "hidden" }}>
      <Typography component="h2" sx={{ color: "primary.900", fontSize: 18, fontWeight: 800 }}>
        Paso 2: Productos y Stock
      </Typography>
      <Summary matching={matching} preview={preview} />
      {notice ? (
        <Typography role="alert" sx={{ p: 1.25, borderRadius: "10px", bgcolor: "warning.light", color: "primary.900", fontSize: 12 }}>
          {notice}
        </Typography>
      ) : null}
      {additionalBlockingIssues.length ? (
        <Stack role="alert" spacing={0.5} sx={{ p: 1.25, borderRadius: "10px", bgcolor: "error.light" }}>
          <Typography sx={{ color: "error.dark", fontSize: 12, fontWeight: 800 }}>
            Hay problemas de costo o composición que deben resolverse antes de continuar:
          </Typography>
          {additionalBlockingIssues.map((issue, index) => (
            <Typography key={`${issue.normalizedDescription || "general"}-${issue.code}-${index}`} sx={{ color: "error.dark", fontSize: 11 }}>
              {issue.message}
            </Typography>
          ))}
        </Stack>
      ) : null}
      <Stack direction="row" spacing={1.25} useFlexGap sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", position: "sticky", top: 0, zIndex: 2, bgcolor: "neutral.50", py: 0.5 }}>
        <Stack direction="row" spacing={1.25} useFlexGap sx={{ minWidth: 0, alignItems: "center", flexWrap: "wrap" }}>
          <Stack direction="row" spacing={0.5} sx={{ maxWidth: "100%", overflowX: "auto", p: 0.35, border: "1px solid", borderColor: "neutral.600", borderRadius: "10px" }}>
            {[["descriptions", "No reconocidos"], ["shortages", `Stock insuficiente (${preview?.summary.shortageCount ?? 0})`]].map(([value, label]) => (
              <Button key={value} type="button" aria-pressed={tab === value} disabled={hasPendingEdit && tab !== value} onClick={() => setTab(value)} sx={{ flexShrink: 0, bgcolor: tab === value ? "primary.500" : "transparent", color: tab === value ? "logoblanco" : "primary.900", textTransform: "none", borderRadius: "8px", fontSize: 12, fontWeight: 750, "&:hover": { bgcolor: tab === value ? "primary.600" : "secondary.100" } }}>
                {label}
              </Button>
            ))}
          </Stack>
          {tab === "descriptions" && !loading && !error ? (
            <Typography sx={{ color: "neutral.700", fontSize: 11 }}>
              Se muestran también las descripciones reconocidas automáticamente para su revisión.
            </Typography>
          ) : null}
        </Stack>
        {tab === "shortages" && !loading && !error && shortages.length > 0 ? (
          planPrepared ? (
            <Typography role="status" sx={{ px: 1.5, py: 0.55, borderRadius: "999px", bgcolor: "success.main", color: "logoblanco", fontWeight: 800, fontSize: 12 }}>Regularizaciones preparadas</Typography>
          ) : (
            <Button
              type="button"
              disabled={saving || !preview?.summary.canContinue || preview?.summary.blockingIssueCount > 0}
              onClick={() => setConfirmOpen(true)}
              sx={{ px: 2, borderRadius: "33px", bgcolor: "primary.500", color: "logoblanco", textTransform: "none", fontWeight: 800, fontSize: 12, "&:hover": { bgcolor: "primary.600" } }}
            >
              Preparar regularizaciones
            </Button>
          )
        ) : null}
      </Stack>
      {hasPendingEdit ? <Typography role="status" sx={{ color: "warning.dark", fontSize: 11 }}>Guardá o corregí los cambios pendientes antes de cambiar de paso o pestaña.</Typography> : null}
      {tab === "descriptions" && optionsError && !loading && !error ? (
        <Stack role="alert" direction="row" spacing={1} sx={{ alignItems: "center", justifyContent: "space-between", p: 1, borderRadius: "10px", bgcolor: "warning.light" }}>
          <Typography sx={{ color: "primary.900", fontSize: 12 }}>{optionsError}</Typography>
          <Button type="button" onClick={onRetryOptions} disabled={optionsLoading} sx={{ textTransform: "none", fontWeight: 800 }}>Reintentar productos</Button>
        </Stack>
      ) : null}
      {saving ? <Typography role="status" sx={{ color: "primary.900", fontSize: 12 }}>Guardando resolución y recalculando Stock…</Typography> : null}
      {loading ? (
        <Stack role="status" direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <CircularProgress size={20} />
          <Typography>Recalculando productos y Stock…</Typography>
        </Stack>
      ) : error ? (
        <Stack spacing={1}>
          <Typography role="alert" sx={{ color: "error.main" }}>{error}</Typography>
          <Button type="button" onClick={onRetry} sx={{ alignSelf: "flex-start", textTransform: "none" }}>Reintentar</Button>
        </Stack>
      ) : (
        <Box sx={{ border: "1px solid", borderColor: "neutral.600", borderRadius: "20px", height: { xs: 300, md: "auto" }, minHeight: { xs: 200, md: 0 }, flex: { md: 1 }, flexShrink: { xs: 0, md: 1 }, overflow: "hidden", bgcolor: "neutral.50" }}>
          {tab === "descriptions"
            ? <DescriptionTable descriptions={preview?.descriptions || matching?.descriptions || []} descriptionOrder={descriptionOrder} options={options} optionsLoading={optionsLoading} optionsError={optionsError} productSearch={productSearch} busy={loading || saving} rememberedResolutions={rememberedResolutions} onSearch={onSearch} onResolve={onResolve} onPendingChange={handlePendingChange} />
            : <ShortagesTable shortages={preview?.shortages || []} />}
        </Box>
      )}
      {tab === "shortages" && !loading && !error ? (
        <Typography sx={{ color: "neutral.700", fontSize: 12 }}>
          No se modificará el Stock hasta integrar las ventas en el último paso.
        </Typography>
      ) : null}
      <SalesImportRegularizationDialog
        open={confirmOpen && !loading && !error}
        shortages={shortages}
        loading={saving}
        error={notice}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onPrepare}
      />
    </Stack>
  );
}
